import { useNavbarContext } from 'context/NavbarContext';
import { useEffect, useState } from 'react';
import { viewEmailThreadDetailsV2 } from 'services/mailbox/mailboxServiceV2';

const DEFAULT_LIMIT = 50;

/**
 * Hook for fetching all emails in a thread (parent + replies) using V2 RDS endpoint.
 * Auto-marks the thread as read when fetched (handled server-side).
 *
 * @param {{ pk: string|null, sk: string|null }} emailThreadPkSk - Selected thread keys
 * @param {Function} setReadEmails - Setter for local read-state tracking
 */
const useEmailThreadDetailsV2 = (emailThreadPkSk, setReadEmails = () => {}) => {
  const { pk: emailThreadPk, sk: emailThreadSk } = emailThreadPkSk || {};
  const [emailDetails, setEmailDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const { notificationData } = useNavbarContext();

  const fetchThreadDetails = async (offset = 0, append = false) => {
    if (!emailThreadPk || !emailThreadSk) return;

    setLoading(true);
    setError(null);

    try {
      const res = await viewEmailThreadDetailsV2(
        encodeURIComponent(emailThreadPk),
        encodeURIComponent(emailThreadSk),
        DEFAULT_LIMIT,
        offset
      );

      const details = res?.data?.email_thread_details ?? [];
      const pagination = res?.data?.pagination ?? {};

      setTotalCount(pagination.total_count ?? 0);
      setHasMore(pagination.has_more ?? false);

      if (append) {
        setEmailDetails((prev) => [...prev, ...details]);
      } else {
        // Reverse so newest reply appears at bottom (chronological order)
        setEmailDetails([...details].reverse());
      }

      // Mark parent thread as read in local state
      if (emailThreadPk) {
        setReadEmails((prev) => ({ ...prev, [emailThreadPk]: true }));
      }
    } catch (err) {
      setError(err);
      console.error('[useEmailThreadDetailsV2] fetch error:', err);
    } finally {
      setLoading(false);
      await notificationData?.loadNotificationCount();
    }
  };

  useEffect(() => {
    if (!emailThreadPk || !emailThreadSk) {
      setEmailDetails([]);
      return;
    }
    fetchThreadDetails(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailThreadPk, emailThreadSk]);

  return {
    emailDetails,
    setEmailDetails,
    loading,
    error,
    totalCount,
    hasMore,
    fetchThreadDetails,
  };
};

export default useEmailThreadDetailsV2;
